import { useEffect, useState, type FC } from "react";

import { Spinner } from "@/components/ui/spinner";

import type { Draft } from "../../../api/types/draft";
import { runStartedAt } from "../workspace/workspaceModel";

/**
 * When the run now writing a draft that already has a document started, and
 * what it last said it was doing, or null when no run is going. A run with no
 * recorded start still counts.
 */
export const revisingSince = (
  draft:
    | Pick<
        Draft,
        | "display_status"
        | "docx_storage_path"
        | "agent_runs"
        | "display_activity"
      >
    | undefined
): { since: number | null; activity: string | null } | null => {
  if (draft?.display_status !== "running" || !draft.docx_storage_path)
    return null;
  return {
    since: runStartedAt(draft),
    activity: draft.display_activity?.activity?.trim() || null
  };
};

export const runMinutes = (since: number, now: number): number =>
  Math.max(1, Math.round((now - since) / 60_000));

type Props = { since: number | null; activity?: string | null };

/**
 * Above a draft the drafting agent is rewriting. The document opens to read
 * while the run goes: its handback replaces these bytes, so edits made now
 * would be lost. The pane reloads with the new document when it lands.
 */
export const DraftRunBanner: FC<Props> = ({ since, activity = null }) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (since === null) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, [since]);

  return (
    <div
      data-testid="draft-run-banner"
      className="flex items-center gap-2 border-b border-line-1 bg-info-soft px-3 py-1.5 text-sm text-fg-2"
    >
      <Spinner className="size-3.5 shrink-0 text-info" />
      <span className="min-w-0 flex-1 truncate">
        <span className="text-fg">Casey is revising this</span>
        {activity ? ` · ${activity}` : null}
        {since === null ? null : ` · ${runMinutes(since, now)} min`}
        {" · Read only until the new version lands"}
      </span>
    </div>
  );
};
